import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, PenSquare, Package, History, Ticket, LogIn, LogOut, User, Shield, FileX, Settings, ClipboardList, CalendarDays, Tickets, Gift, X, FileText, Download, PartyPopper, PauseCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { useGridEnterNavigation } from "@/hooks/useGridEnterNavigation";
import { useDragRangeSelection } from "@/hooks/useDragRangeSelection";
import { StoreSwitcher } from "@/components/StoreSwitcher";
import { LotteryTabs } from "@/components/LotteryTabs";
import { toast } from "sonner";
import axios from "axios";

const PAGE_TRANSITION = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.3, ease: "easeOut" }
};

// Lottery-related pages (entry/dashboard/stock/history) share ONE nav item;
// switching between them happens via in-page tabs (LotteryTabs).
const navItems = [
  { to: "/worksheet", icon: ClipboardList, label: "Worksheet" },
  { to: "/schedule", icon: CalendarDays, label: "Schedule" },
  { to: "/coupon", icon: Tickets, label: "Coupon" },
  { to: "/store-paperwork", icon: FileText, label: "Store Paperwork", flag: "store_paperwork_enabled" },
  { to: "/fun-time", icon: PartyPopper, label: "Fun Time", access: "fun_time" },
  { to: "/exports", icon: Download, label: "Exports" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

// Persistent yellow promo banner — dismissed for the rest of the session via sessionStorage.
const FreePromoBanner = () => {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("cyl_free_banner_dismissed") === "1"; }
    catch { return false; }
  });
  if (dismissed) return null;
  const close = () => {
    setDismissed(true);
    try { sessionStorage.setItem("cyl_free_banner_dismissed", "1"); } catch { /* noop */ }
  };
  return (
    <div
      className="bg-yellow-300 text-yellow-950 border-b border-yellow-500 print:hidden"
      data-testid="free-promo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-mono text-sm font-semibold">
          <Gift className="w-4 h-4 flex-shrink-0" strokeWidth={2.25} />
          <span>Welcome! Enjoy Control Your Lottery <strong>free until the end of 2026</strong> — <strong>no credit card required</strong> 🎉</span>
        </div>
        <button
          onClick={close}
          className="p-1 hover:bg-yellow-400 rounded transition-colors flex-shrink-0"
          aria-label="Dismiss banner"
          data-testid="free-promo-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, loading } = useAuth();
  const { flags } = useFeatureFlags();
  // Server-resolved visibility for the Fun Time tab (mode lives in the DB).
  const funTimeAccess = useFeatureAccess("fun_time");
  // Excel-style Enter-to-next-cell navigation everywhere in the logged-in shell.
  useGridEnterNavigation();
  // Excel-style click + drag rectangular cell selection + copy/paste.
  useDragRangeSelection({
    onCopy: (n) => toast.success(`Copied ${n} cell${n > 1 ? "s" : ""}`),
    onPaste: (n, r, c) => toast.success(`Pasted ${r}×${c} (${n} cell${n > 1 ? "s" : ""})`),
  });
  const isAdmin = user?.role === "admin";
  // Resolve the currently active store from the user's stores array. Falls
  // back to legacy single-store fields for backward compat with anything the
  // backfill hasn't touched yet.
  const activeStore = (() => {
    const stores = user?.stores || [];
    const active = stores.find((s) => s.id === user?.active_store_id) || stores[0];
    if (active) return active;
    return { name: user?.store_name || "", address: user?.store_address || "" };
  })();
  // Filter nav items by their feature flag — admins always see flagged items;
  // regular users only see them when the flag is enabled by the admin.
  // Items with an `access` key are resolved server-side (feature-access model).
  const accessMap = { fun_time: funTimeAccess.canAccess };
  const visibleNavItems = navItems.filter((item) => {
    if (item.access) return !!accessMap[item.access];
    return !item.flag || isAdmin || flags?.[item.flag];
  });
  const [clearType, setClearType] = useState(null); // 'page' or 'all'
  const [clearStep, setClearStep] = useState(0); // 0=closed, 1=first confirm, 2=second confirm
  const [confirmText, setConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [support, setSupport] = useState({ name: 'Bob', phone: '859-537-5780' });
  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch global support contact (public endpoint, no auth needed)
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/public/support-contact`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled || !d) return;
        setSupport({ name: d.name || 'Bob', phone: d.phone || '859-537-5780' });
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, [API]);

  // Extract current date from URL if on entry page
  const getCurrentDate = () => {
    const match = location.pathname.match(/\/entry\/(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  const isOnEntryPage = location.pathname.startsWith('/entry');

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const openClearDialog = (type) => {
    setClearType(type);
    setClearStep(1);
    setConfirmText('');
  };

  const closeClearDialog = () => {
    setClearType(null);
    setClearStep(0);
    setConfirmText('');
  };

  const handleFirstConfirm = () => {
    setClearStep(2);
    setConfirmText('');
  };

  const handleFinalClear = async () => {
    const requiredEmail = (user?.email || '').trim().toLowerCase();
    if (confirmText.trim().toLowerCase() !== requiredEmail) return;
    setClearing(true);
    try {
      if (clearType === 'page') {
        const date = getCurrentDate();
        if (!date) {
          toast.error("No date selected");
          return;
        }
        await axios.delete(`${API}/api/entry/${date}`);
        toast.success(`Cleared entry for ${date} — recoverable from Admin → Recovery for 30 days`);
      } else {
        const res = await axios.delete(`${API}/api/entries/clear-all`, {
          params: { confirm_email: user?.email },
        });
        toast.success(`Cleared ${res.data.deleted_count} entries — recoverable from Admin → Recovery for 30 days`);
      }
      closeClearDialog();
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear data");
    } finally {
      setClearing(false);
    }
  };

  // Redirect to login if not authenticated
  if (loading) return null;
  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  const clearDialogTitle = clearType === 'page'
    ? `Clear This Page (${getCurrentDate() || 'current date'})?`
    : 'Clear ALL Data?';

  const clearDialogDesc1 = clearType === 'page'
    ? `This will permanently delete the entry for ${getCurrentDate() || 'this date'}. This cannot be undone.`
    : 'This will permanently delete ALL your entries across all dates. This cannot be undone.';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-card px-4 md:px-8 py-4 print:hidden shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-12">
          <div className="flex items-center gap-3 min-w-0 relative z-20">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shadow-md flex-shrink-0">
              <Ticket className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-shrink-0">
              <h1 className="font-mono text-base sm:text-xl font-bold tracking-tight text-card-foreground">Lottery Management</h1>
              <p className="text-xs text-muted-foreground whitespace-nowrap">Terminal | <span data-testid="help-contact">For help call {support.name} {support.phone}</span></p>
            </div>
            {/* Store switcher — only rendered when the admin has assigned 2+ stores.
                Users with a single store see nothing at all. */}
            {isAuthenticated && <StoreSwitcher variant="header" />}
          </div>
          
          {/* Navigation - pushed to right edge */}
          <nav className="flex items-center gap-1 overflow-x-auto -mx-2 px-2 md:overflow-visible md:mx-0 md:px-0 md:flex-wrap md:ml-auto md:justify-end">
            {/* Daily Lottery — Dashboard/Stock/History are tabs inside the page */}
            {isAuthenticated && (
              <NavLink
                to="/entry"
                className={() => {
                  const p = location.pathname;
                  const active = p === "/" || p.startsWith("/entry") || p.startsWith("/stock") || p.startsWith("/history");
                  return `nav-3d flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg font-mono text-xs sm:text-sm flex-shrink-0 ${active ? "active" : ""}`;
                }}
                data-testid="nav-lottery-group"
              >
                <PenSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
                <span className="hidden md:inline">Daily Lottery</span>
              </NavLink>
            )}
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `nav-3d flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg font-mono text-xs sm:text-sm flex-shrink-0 ${
                    isActive ? "active" : ""
                  }`
                }
              >
                <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
                <span className="hidden md:inline">{item.label}</span>
              </NavLink>
            ))}
            
            {/* Admin Link - only for admin users */}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `nav-3d flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg font-mono text-xs sm:text-sm flex-shrink-0 ${
                    isActive ? "active" : ""
                  }`
                }
              >
                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
                <span className="hidden md:inline">Admin</span>
              </NavLink>
            )}
            
            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="ml-2 gap-2 hover:bg-secondary h-auto py-1.5" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="hidden md:flex flex-col items-start min-w-0 max-w-[180px]">
                      <span
                        className="font-mono text-sm text-card-foreground font-semibold truncate w-full text-left"
                        title={activeStore?.name || user?.name}
                        data-testid="header-store-name"
                      >
                        {activeStore?.name?.trim() || user?.name}
                      </span>
                      {activeStore?.address?.trim() && (
                        <span
                          className="font-mono text-[10px] text-muted-foreground truncate w-full text-left leading-tight"
                          title={activeStore.address}
                          data-testid="header-store-address"
                        >
                          {activeStore.address}
                        </span>
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-2 py-1.5">
                    {activeStore?.name?.trim() ? (
                      <>
                        <p className="font-mono text-sm font-bold text-card-foreground">{activeStore.name}</p>
                        {activeStore.address?.trim() && (
                          <p className="text-[11px] text-muted-foreground leading-tight mb-1" data-testid="menu-store-address">
                            {activeStore.address}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground font-mono">{user.name}</p>
                      </>
                    ) : (
                      <p className="font-mono text-sm font-medium">{user?.name}</p>
                    )}
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {/* Store switcher (menu variant) */}
                  <StoreSwitcher variant="menu" />
                  <DropdownMenuSeparator />
                  {isOnEntryPage && getCurrentDate() && (
                    <DropdownMenuItem onClick={() => openClearDialog('page')} className="text-orange-500 cursor-pointer" data-testid="clear-page-menu-btn">
                      <FileX className="w-4 h-4 mr-2" />
                      Clear This Page
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="ml-2 gap-2 text-muted-foreground hover:text-primary"
                data-testid="login-btn"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden md:inline">Sign In</span>
              </Button>
            )}
          </nav>
        </div>
      </header>

      {/* Free until end of 2026 promo banner */}
      {isAuthenticated && (
        <FreePromoBanner />
      )}

      {/* Paused account — view-only banner */}
      {isAuthenticated && user?.is_paused && (
        <div className="bg-amber-400 text-amber-950 border-b border-amber-600 print:hidden" data-testid="paused-banner">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2 font-mono text-sm font-semibold">
            <PauseCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2.25} />
            <span>
              Your account is paused — <strong>view-only</strong>. Saving and data entry are disabled
              {user.pause_until ? <> until <strong>{user.pause_until}</strong></> : ""}. Contact your admin to resume.
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 p-3 sm:p-4 md:p-8 print:p-0">
        <motion.div
          {...PAGE_TRANSITION}
          className="max-w-7xl mx-auto"
        >
          {isAuthenticated && (location.pathname === "/" || ["/entry", "/stock", "/history", "/lotto-scan"].some((r) => location.pathname.startsWith(r))) && (
            <LotteryTabs />
          )}
          <Outlet />
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 px-4 py-4 print:hidden">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs text-foreground/60 font-mono">
            Lottery Management v1.1 | Auto-carry forward enabled
            {isAuthenticated && ` | Logged in as ${user?.name}`}
          </p>
        </div>
      </footer>

      {/* Double Confirmation Clear Dialog */}
      <Dialog open={clearStep > 0} onOpenChange={(open) => { if (!open) closeClearDialog(); }}>
        <DialogContent>
          {clearStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle>{clearDialogTitle}</DialogTitle>
                <DialogDescription>{clearDialogDesc1}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={closeClearDialog}>Cancel</Button>
                <Button variant="destructive" onClick={handleFirstConfirm} data-testid="clear-first-confirm">
                  Yes, I&apos;m Sure
                </Button>
              </DialogFooter>
            </>
          )}
          {clearStep === 2 && (
            <>
              <DialogHeader>
                <DialogTitle>Final Confirmation</DialogTitle>
                <DialogDescription>
                  Type your account email <span className="font-bold text-destructive">{user?.email}</span> below to proceed. This action moves the data to the 30-day recovery bin — restorable from Admin → Recovery.
                </DialogDescription>
              </DialogHeader>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={user?.email || "Type your email"}
                className="font-mono"
                data-testid="clear-confirm-input"
                autoFocus
              />
              <DialogFooter>
                <Button variant="ghost" onClick={closeClearDialog}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={handleFinalClear}
                  disabled={confirmText.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase() || clearing}
                  data-testid="clear-final-confirm"
                >
                  {clearing ? "Clearing..." : clearType === 'page' ? "Clear This Page" : "Clear Everything"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
